import { Injectable } from "@angular/core";
import { lastValueFrom, timer } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { retry } from "rxjs/operators";
import { Device } from "@capacitor/device";
import { Directory } from "@capacitor/filesystem";
import { Router } from "@angular/router";
import { handlePromise } from "../../utils";
import { EnvironmentService } from "./environment.service";
import { RequestRepository } from "../repositories/request.repository";
import { ImageFile } from "../types/Image/ImageFile";
import { FileService } from "./file.service";
import { AuthCredentials } from "../types/Auth/Auth";
import { RequestSearchPage } from "../types/Request/RequestSearchPage";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { AuthRepository } from "../repositories/auth.repository";
import { DownloadedClientRepository } from "../repositories/downloaded.client.repository";
import { AvailableClientRepository } from "../repositories/available.client.repository";
import { RequestData } from "../types/Request/RequestData";

@Injectable({
  providedIn: "root",
})
export class RequestService extends BaseService {
  requestData: RequestData;

  constructor(
    private http: HttpClient,
    private fileService: FileService,
    private environmentService: EnvironmentService,
    private requestRepository: RequestRepository,
    private authRepo: AuthRepository,
    private downloadedClientRepo: DownloadedClientRepository,
    private router: Router,
    private availableClientRepo: AvailableClientRepository,
    logService: LogService,
  ) {
    super(logService, "RequestService");
    this.requestRepository
      .get()
      .then((data) => {
        this.requestData = data || {};
      })
      .catch((err) => {
        this.logger.error(err);
      });
  }

  public retryRequestOnTimeout(request: any, delay = 2000): Promise<void> {
    const requestWithRetry = request.pipe(
      retry({
        count: 2,
        delay: (error) => {
          if ([0, 500, 504].includes(error.status)) {
            return timer(delay);
          }
          throw error;
        },
        resetOnSuccess: true,
      }),
    );
    return lastValueFrom(requestWithRetry);
  }

  /**
   * Initializes the request data by fetching it from the request repository.
   * @returns {Promise<void>} A promise that resolves when the request data is initialized.
   */
  public async requestDataInit(): Promise<void> {
    this.requestData = (await this.requestRepository.get()) || {};
  }

  /**
   * Retrieves the current device information.
   * @returns {Promise<string>} A promise that resolves  a string with the current device information.
   */
  public async getCurrentDeviceInfo(): Promise<string> {
    const deviceInfo = await Device.getInfo();
    const { identifier: uuid } = await Device.getId();
    return JSON.stringify({
      uuid,
      ...deviceInfo,
    });
  }

  /**
   * Sanitizes a URL by removing the leading forward slash if it exists.
   * @param {string} url - The URL to sanitize.
   * @returns {string} The sanitized URL without a leading forward slash.
   */
  public sanitizeUrl(url: string): string {
    if (url.startsWith("/")) {
      return url.slice(1);
    }
    return url;
  }

  /**
   * Sets the authentication credentials in the request data and updates the request repository.
   * @param {AuthCredentials} auth - The authentication credentials to set.
   * @returns {Promise<void>} A promise that resolves when the authentication credentials have been set.
   */
  public async setAuth(auth: AuthCredentials): Promise<void> {
    if (!this.requestData) {
      await this.requestDataInit();
    }
    this.requestData.sessionID = auth.sessionID;
    this.requestData.env = auth.env;
    await this.requestRepository.set(this.requestData);
  }

  /**
   * Retrieves the authentication data.
   * @returns {Promise<RequestData>} The current request data containing request authentication information.
   */
  public async getAuth(): Promise<RequestData> {
    if (this.requestData) {
      return this.requestData;
    }
    const res = await this.requestRepository.get();
    return res;
  }

  /**
   * Sets the client ID in the request data and updates the request repository.
   * @param {string} clientID - The client ID to set.
   * @returns {Promise<void>} A promise that resolves when the client ID has been set.
   */
  public async setClient(clientID: string): Promise<void> {
    this.requestData.clientID = clientID;
    await this.requestRepository.set(this.requestData);
  }

  /**
   * Sets the search variables in the request data and updates the request repository.
   * @param {RequestSearchPage[]} pages - The search pages to set in the request data.
   * @returns {Promise<void>} A promise that resolves when the request data is updated.
   */
  public async setSearchVariables(pages: RequestSearchPage[]): Promise<void> {
    this.requestData.pages = pages;
    await this.requestRepository.set(this.requestData);
  }

  /**
   * Clears the request data and the request repository.
   * @returns {Promise<void>} A promise that resolves when the request data and repository are cleared.
   */
  public clearRequestData(): Promise<void> {
    this.requestData = undefined as any;
    return this.requestRepository.clear();
  }

  /**
   * Logs out the current user by clearing authentication and client data,
   * and then navigates to the login page.
   * @returns {Promise<boolean>} A promise that resolves to a boolean indicating
   * whether the navigation to the login page was successful.
   */
  public async logout(): Promise<boolean> {
    const auth = await this.authRepo.get();
    await this.authRepo.set({ email: auth.email, env: auth.env } as any);
    await this.clearRequestData();
    await this.downloadedClientRepo.setCurrentClientId(undefined);
    await this.availableClientRepo.clear();
    return this.router.navigate(["/login"]);
  }

  /**
   * Converts an object to FormData.
   * @param {any} obj - The object to convert to FormData.
   * @param {FormData} [form] - The FormData instance to append to. Defaults to a new FormData instance.
   * @param {string} [namespace] - The namespace for the FormData keys. Defaults to an empty string.
   * @returns {FormData} The resulting FormData object.
   */
  public objectToFormData = (
    obj: any,
    form = new FormData(),
    namespace = "",
  ) => {
    const isArray = Array.isArray(obj);

    Object.entries(obj).forEach(([property, value]: any) => {
      // eslint-disable-next-line no-nested-ternary
      const formKey = namespace
        ? isArray
          ? `${namespace}[]`
          : `${namespace}[${property}]`
        : property;

      if (typeof value === "object" && !(value instanceof File)) {
        this.objectToFormData(value, form, formKey);
      } else {
        form.append(formKey, value);
      }
    });

    return form;
  };

  /**
   * Sends a POST request to the specified endpoint with the provided body.
   * @param {string} endpoint - The endpoint to send the POST request to.
   * @param {any} [body] - The body of the POST request. Defaults to an empty object.
   * @param {boolean} [isSearch] - Whether the request is a search request. Defaults to false.
   * @param {boolean} [logoutOnUnauthorized] - Whether to log out the user if the request is unauthorized. Defaults to true.
   * @param {boolean} [silent] - Whether to suppress error logging. Defaults to false.
   * @returns {Promise<any>} A promise that resolves with the response data or an error object.
   * @throws {Error} If no environment is set in the request data.
   */
  public async post(
    endpoint: string,
    body: any = {},
    isSearch: boolean = false,
    logoutOnUnauthorized = true,
    silent: boolean | ((err: any) => boolean) = false,
  ): Promise<any> {
    if (!this?.requestData?.env) {
      throw new Error("No environment set");
    }
    const sanitizedEndpoint = this.sanitizeUrl(endpoint);
    const url = this.environmentService.getAdminPortalUrl(this.requestData.env);
    const postBody = body;
    if (body instanceof FormData) {
      postBody.append("auth", this.requestData.sessionID);
      postBody.append("clientID", this.requestData.clientID);
    } else {
      postBody.auth = this.requestData.sessionID;
      postBody.clientID = this.requestData.clientID;
      if (isSearch) {
        postBody.pages = this.requestData.pages;
      }
    }
    const observable = this.http.post(url + sanitizedEndpoint, postBody, {
      params: { "ngsw-bypass": true },
    });
    const [err, res]: any = await handlePromise(
      this.retryRequestOnTimeout(observable, 2000),
    );
    if (err) {
      if (err.status === 401 && !logoutOnUnauthorized) {
        throw err;
      }
      const shouldLog =
        (typeof silent === "boolean" && !silent) ||
        (typeof silent === "function" && !silent(err));
      if (shouldLog) {
        if (err.status === 0 && err.error instanceof ProgressEvent) {
          this.logger.error("Network Error On Post Request", {
            details: {
              ...err,
              error: {
                type: err.error.type,
                total: err.error.total,
                lengthComputable: err.error.lengthComputable,
                loaded: err.error.loaded,
              },
            },
          });
        } else {
          this.logger.error("Network Error On Post Request", {
            details: err,
          });
        }
      }
      if (err.status === 401 && logoutOnUnauthorized) {
        await this.logout();
      }
      return err.error;
    }
    return res;
  }

  /**
   * Sends a GET request to the specified endpoint with optional parameters.
   * @param {string} endpoint - The endpoint to send the GET request to.
   * @param {any} [params] - Optional parameters to include in the request.
   * @param {boolean} [logoutOnUnauthorized] - Whether to log out the user if the request is unauthorized.
   * @param {boolean} [silent] - Whether to suppress error logging. Defaults to false.
   * @returns {Promise<any>} A promise that resolves with the response data or an error object.
   * @throws {Error} If no environment is set in the request data.
   */
  public async get(
    endpoint: string,
    params?: any,
    logoutOnUnauthorized = true,
    silent: boolean | ((err: any) => boolean) = false,
  ): Promise<any> {
    if (!this?.requestData?.env) {
      throw new Error("No environment set");
    }
    const sanitizedEndpoint = this.sanitizeUrl(endpoint);
    const url = this.environmentService.getAdminPortalUrl(this.requestData.env);
    const paramsWithAuth = {
      ...params,
      auth: this.requestData.sessionID,
      clientID: this.requestData.clientID,
      "ngsw-bypass": true,
    };
    const urlEncodedParams = Object.entries(paramsWithAuth).reduce(
      (acc: any, [key, value]: [string, any]) => {
        if (typeof value === "object") {
          return { ...acc, [key]: JSON.stringify(value) };
        }
        return { ...acc, [key]: value };
      },
      {},
    );
    const observable = this.http.get(url + sanitizedEndpoint, {
      params: urlEncodedParams,
    });
    const [err, res]: any = await handlePromise(
      this.retryRequestOnTimeout(observable, 2000),
    );
    if (err) {
      if (err.status === 401 && !logoutOnUnauthorized) {
        throw err;
      }
      const shouldLog =
        (typeof silent === "boolean" && !silent) ||
        (typeof silent === "function" && !silent(err));
      if (shouldLog) {
        if (err.status === 0 && err.error instanceof ProgressEvent) {
          this.logger.error("Network Error On Get Request", {
            details: {
              ...err,
              error: {
                type: err.error.type,
                total: err.error.total,
                lengthComputable: err.error.lengthComputable,
                loaded: err.error.loaded,
              },
            },
          });
        } else {
          this.logger.error("Network Error On Get Request", {
            details: err,
          });
        }
      }
      if (err.status === 401 && logoutOnUnauthorized) {
        await this.logout();
      }
      return err.error;
    }
    return res;
  }

  /**
   * Sends a POST request to the specified Services API endpoint.
   * @param {string} endpoint - The API endpoint to send the request to.
   * @param {any} [body] - The body of the request. Defaults to an empty object.
   * @param {any} [options] - Additional options for the request. Defaults to an empty object.
   * @returns {Promise<any>} A promise that resolves with the response from the API.
   */
  public postApi(
    endpoint: string,
    body: any = {},
    options: any = {},
  ): Promise<any> {
    const sanitizedEndpoint = this.sanitizeUrl(endpoint);
    const url = this.environmentService.getServicesApiUrl(this.requestData.env);
    const postBody = body;
    if (body instanceof FormData) {
      postBody.append("authKey", this.requestData.sessionID);
    } else {
      postBody.authKey = this.requestData.sessionID;
    }
    return lastValueFrom(
      this.http.post(url + sanitizedEndpoint, postBody, {
        ...options,
        params: {
          "ngsw-bypass": true,
          ...options.params,
        },
      }),
    ) as any;
  }

  /**
   * Sends a GET request to the specified Services API endpoint.
   * @param {string} endpoint - The API endpoint to send the request to.
   * @param {any} [params] - The query parameters for the request. Defaults to an empty object.
   * @param {any} [options] - Additional options for the request. Defaults to an empty object.
   * @returns {Promise<any>} A promise that resolves with the response from the API.
   */
  public getApi(
    endpoint: string,
    params: any = {},
    options: any = {},
  ): Promise<any> {
    const sanitizedEndpoint = this.sanitizeUrl(endpoint);
    const url = this.environmentService.getServicesApiUrl(this.requestData.env);
    return lastValueFrom(
      this.http.get(url + sanitizedEndpoint, {
        ...options,
        params: {
          ...params,
          authKey: this.requestData.sessionID,
          clientID: this.requestData.clientID,
          "ngsw-bypass": true,
        },
      }),
    ) as any;
  }

  /**
   * Downloads a file from the specified URL and writes it to the specified path.
   * @param {string} url - The URL to download the file from.
   * @param {string} path - The path to write the downloaded file to.
   * @param {Directory} [directory] - The directory to write the file to. Defaults to Directory.Data.
   * @returns {Promise<ImageFile>} A promise that resolves with the downloaded file.
   */
  public async downloadFile(
    url: string,
    path: string,
    directory: Directory = Directory.Data,
  ): Promise<ImageFile> {
    const resolvedUrl = this.resolveDownloadUrl(url);
    const fileBlob: any = await lastValueFrom(
      this.http
        .get(resolvedUrl, {
          responseType: "blob",
          params: { "ngsw-bypass": true },
        })
        .pipe(retry({ count: 2, delay: 2000, resetOnSuccess: true })),
    );
    return this.fileService.writeFile(fileBlob, path, directory);
  }

  private resolveDownloadUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
    if (!normalizedUrl.startsWith("/hscloudsuite-printables/")) {
      return normalizedUrl;
    }
    const printablesBaseUrl = this.environmentService.getPrintablesUrl();
    if (!printablesBaseUrl) {
      return normalizedUrl;
    }
    const normalizedBase = printablesBaseUrl.endsWith("/")
      ? printablesBaseUrl
      : `${printablesBaseUrl}/`;

    const printablePath = normalizedUrl.replace(
      /^\/hscloudsuite-printables\/?/,
      "",
    );
    return `${normalizedBase}${printablePath}`;
  }

  /**
   * Uploads a file to the specified API endpoint.
   * @param {string} endpoint - The API endpoint to upload the file to.
   * @param {any} data - Additional data to include in the upload request.
   * @param {File} file - The file to upload.
   * @returns {Promise<any>} A promise that resolves with the response from the API.
   */
  public async uploadFile(
    endpoint: string,
    data: any,
    file: File,
  ): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("auth", this.requestData.sessionID);
    formData.append("clientID", this.requestData.clientID);
    Object.entries(data).forEach(([key, value]: any) => {
      formData.append(key, value);
    });
    const sanitizedEndpoint = this.sanitizeUrl(endpoint);
    const url = this.environmentService.getAdminPortalUrl(this.requestData.env);
    const observable = this.http.post(url + sanitizedEndpoint, formData);
    const [err, res]: any = await handlePromise(
      this.retryRequestOnTimeout(observable, 2000),
    );
    if (err) {
      this.logger.error(err);
      return err.error;
    }
    return res;
  }
}
